import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { GET_TEMPLATES } from '../../contracts';
import { getTemplates } from '../api/templates';
import { getTemplatesDataConfig } from '../config';

type Templates = Awaited<ReturnType<typeof getTemplates>>['data']['templates'];

function getCachedTemplates(): Templates | undefined {
  return getTemplatesDataConfig().queryClient.getQueryData<Templates>([GET_TEMPLATES]);
}

/**
 * The legacy template list. `onRequestError` is the severed toast: the app
 * raises it at the same position in the catch, so the offline branch still
 * returns before it.
 */
export function useQueryTemplates(onRequestError?: (error: AxiosError) => void) {
  return useQuery({
    queryKey: [GET_TEMPLATES],
    queryFn: async () => {
      try {
        const { data } = await getTemplates();
        return data.templates;
      } catch (error) {
        if (error instanceof AxiosError) {
          if (getTemplatesDataConfig().isOfflineNetworkError(error)) {
            const cached = getCachedTemplates();
            if (cached) {
              return cached;
            }
            // No cache to fall back on — rethrow so the query enters an error
            // state and refetches on reconnect, rather than caching an empty
            // success that the global staleTime would treat as fresh.
            throw error;
          }

          onRequestError?.(error);
        }
      }
    },
  });
}
